import {language,translate,updateAlternates} from './site-languages.js?v=token-fix-20261009';
import {localePath} from './locale-copy.js';
import { pairCopy } from './swap-copy.js?v=languages-1';
import {pairIsAvailable} from './pair-availability.js?v=token-fix-20261009';

export const currencyKey = coin => `${coin.ticker}_${coin.network}`;
// A catalog is immutable for the lifetime of a load. Index it once instead of
// scanning all networks for every one of the directory's token pairs.
const currencyIndexes = new WeakMap();
function currencyIndex(coins) {
  if (!currencyIndexes.has(coins)) {
    const index = new Map();
    for (const coin of coins) {
      const ticker = coin.ticker.toUpperCase();
      if (!index.has(ticker)) index.set(ticker, []);
      index.get(ticker).push(coin);
    }
    currencyIndexes.set(coins,index);
  }
  return currencyIndexes.get(coins);
}

export function swapUrl(from, to) {

  const query = new URLSearchParams({ fromNetwork: from.network, toNetwork: to.network });

  return `/swap/${encodeURIComponent(from.ticker.toUpperCase())}-${encodeURIComponent(to.ticker.toUpperCase())}?${query}`;

}

export function resolvePair(coins, slug, search = '') {

  const parts = String(slug || '').split('-');

  if (parts.length !== 2) return null;

  const query = new URLSearchParams(search);

  const choose = (ticker, network) => {

    const matches = currencyIndex(coins).get(ticker.toUpperCase()) || [];

    if (network !== null) return matches.filter(c => c.network.toLowerCase() === network.toLowerCase());

    // Keep the advertised stablecoin route on its tested default network.
    // Other USDT networks require an explicit selection, not silent fallback.
    if (ticker.toUpperCase() === 'USDT') return [matches.find(c => c.network.toLowerCase() === 'erc20') || matches[0]].filter(Boolean);

    // Stable, familiar defaults; all other networks remain selectable.

    return matches.slice().sort((a,b) => Number(!!b.nativeNetwork)-Number(!!a.nativeNetwork) || Number(b.network.toLowerCase()==='erc20')-Number(a.network.toLowerCase()==='erc20'));

  };

  const fromCoins = choose(parts[0], query.get('fromNetwork'));

  const toCoins = choose(parts[1], query.get('toNetwork'));

  for (const from of fromCoins) for (const to of toCoins) {
    if (currencyKey(from) !== currencyKey(to) && pairIsAvailable(currencyKey(from),currencyKey(to))) return {from,to};
  }
  return null;

}



export function createSwapPages(React, useLocation, useNavigate, useCurrencies, SwapForm, Link) {

  const h = React.createElement;

  function CommonPairs() {

    return h('section', { id: 'buy-crypto', className: 'pair-links' }, h('h2', null, 'Explore swaps'),
      h('p', { className: 'pair-links-description' }, 'Choose a pair to compare providers and find your exchange rate.'),
      h('div', null, ['BTC-USDT', 'ETH-USDT', 'BTC-ETH', 'SOL-USDT', 'XMR-BTC'].map(pair =>
        h(Link, { key: pair, to: `/swap/${pair}` },
          ...pair.split('-').flatMap((ticker, index) => [
            ...(index ? [h('span', { key: 'to', className: 'pair-direction' }, 'to')] : []),
            h('span', { key: ticker, className: 'pair-coin' },
              h('img', { src: `https://trocador.app/static/img/icons/${ticker.toLowerCase()}.svg`, alt: '', width: 32, height: 32, loading: 'lazy', onError: e => { e.currentTarget.style.visibility = 'hidden'; } }), ticker)
          ])))),
      h(Link, { className: 'pair-all', to: '/swap' }, 'Browse all pairs →'));

  }

  function Page() {

    const location = useLocation();

    const navigate = useNavigate();

    const { currencies, loading, error } = useCurrencies();

    const [search, setSearch] = React.useState('');

    const [limit, setLimit] = React.useState(60);

    const slug = location.pathname.split('/')[2] || '';

    const pair = resolvePair(currencies, slug, location.search);

    const copy = pair ? pairCopy(pair.from, pair.to, currencies) : null;

    const title = !slug ? translate('Explore token swaps') : pair ? copy.title : translate(loading ? 'Loading swap…' : 'Pair unavailable');

    React.useEffect(() => {

      const oldTitle = document.title;

      const canonical = document.querySelector('link[rel="canonical"]');

      const description = document.querySelector('meta[name="description"]');

      const oldCanonical = canonical?.getAttribute('href');

      const oldDescription = description?.content;

      const metaChanges = [];

      const setMeta = (selector, value) => { const element = document.querySelector(selector); if (element) { metaChanges.push([element, element.content]); element.content = value; } };

      document.title = `${title} | GhostSwap`;

      canonical?.setAttribute('href', `https://www.ghostswap.app${localePath(pair ? '/swap/' + pair.from.ticker.toUpperCase() + '-' + pair.to.ticker.toUpperCase() : '/swap',language)}`);

      if (description) description.content = copy ? copy.description : translate('Browse supported tokens and choose a swap pair on GhostSwap.');

      for (const selector of ['meta[property="og:title"]','meta[name="twitter:title"]']) setMeta(selector, document.title);

      for (const selector of ['meta[property="og:description"]','meta[name="twitter:description"]']) setMeta(selector, description?.content || '');

      setMeta('meta[property="og:url"]', canonical?.href || 'https://www.ghostswap.app/swap');

      setMeta('meta[name="robots"]', (!loading && slug && !pair) || (pair && pair.from.ticker.toUpperCase() === pair.to.ticker.toUpperCase()) ? 'noindex, follow' : 'index, follow');

      updateAlternates(pair ? '/swap/'+pair.from.ticker.toUpperCase()+'-'+pair.to.ticker.toUpperCase() : '/swap',!slug || !!pair && pair.from.ticker!==pair.to.ticker);
      const existing = document.getElementById('swap-breadcrumb');

      const schema = existing || document.createElement('script');

      const oldSchema = schema.textContent;

      schema.id = 'swap-breadcrumb'; schema.type = 'application/ld+json';

      schema.textContent = JSON.stringify({'@context':'https://schema.org','@type':'BreadcrumbList',itemListElement:[{'@type':'ListItem',position:1,name:translate('Home'),item:'https://www.ghostswap.app'+localePath('/',language)},{'@type':'ListItem',position:2,name:translate('Swap'),item:'https://www.ghostswap.app'+localePath('/swap',language)},...(pair ? [{'@type':'ListItem',position:3,name:title,item:canonical?.href}] : [])]});

      if (!existing) document.head.appendChild(schema);

      return () => { document.title = oldTitle; if (canonical && oldCanonical) canonical.href = oldCanonical; if (description) description.content = oldDescription; metaChanges.forEach(([element,value]) => element.content = value); if (existing) schema.textContent = oldSchema; else schema.remove();


      };

    }, [title, location.search, loading]);

    const openPair = (fromKey, toKey, amount) => {

      const from = currencies.find(c => currencyKey(c) === fromKey);

      const to = currencies.find(c => currencyKey(c) === toKey);

      if (from && to && fromKey !== toKey) navigate(swapUrl(from, to), { state: { amount } });

    };

    const directoryPairs = React.useMemo(() => {
      const pairs = new Map();
      if (slug) return pairs;
      for (const ticker of new Set(currencies.map(c => c.ticker.toUpperCase()))) {
        const candidate=resolvePair(currencies,`${ticker}-${ticker==='USDT'?'BTC':'USDT'}`);
        if (candidate) pairs.set(ticker,candidate);
      }
      return pairs;
    },[currencies,slug]);
    const unique = [...directoryPairs.keys()];

    const query = search.trim().toLowerCase();
    const filtered = !query ? unique : unique.filter(ticker => ticker.toLowerCase().includes(query) || currencyIndex(currencies).get(ticker)?.some(c => c.name.toLowerCase().includes(query)));

    return h('div', { className: 'swap-page' },

      h('nav', { className: 'pair-breadcrumb', 'aria-label': 'Breadcrumb' }, h(Link, { to: '/' }, 'Home'), ' / ', h(Link, { to: '/swap' }, 'Swap'), slug && ` / ${slug}`),

      h('header', { className: 'pair-heading' }, h('p', { className: 'pair-eyebrow' }, 'GHOSTSWAP EXCHANGE'), h('h1', null, title),

        h('p', null, pair ? 'Choose your networks, compare providers, and find your rate.' : 'Find a token and start with a pair. You can change either currency before comparing rates.')),

      loading ? h('p', { role: 'status', className: 'pair-message' }, 'Loading supported tokens…') : error || !currencies.length ? h('div', { className: 'pair-message', role: 'alert' }, 'The token list is unavailable. ', h('button', { onClick: () => window.location.reload() }, 'Retry')) :

        !slug ? h('section', { className: 'pair-directory' },

          h('input', { type: 'search', value: search, placeholder: 'Search token or ticker', 'aria-label': 'Search tokens', onChange: e => { setSearch(e.target.value); setLimit(60); } }),

          h('div', { className: 'pair-token-grid' }, filtered.slice(0, limit).map(ticker => {
            const routePair=directoryPairs.get(ticker);
            const coin = routePair.from;
            return h(Link, { key: ticker, to: swapUrl(routePair.from,routePair.to), className: 'token-swap-card' },
              h('span', { className: 'token-swap-logo', 'aria-hidden': true },
                h('span', { className: 'token-logo-fallback' }, ticker.slice(0, 2)),
                h('img', { src: coin.image || `https://trocador.app/static/img/icons/${encodeURIComponent(ticker.toLowerCase())}.svg`, alt: '', width: 44, height: 44, loading: 'lazy', onLoad: e => { e.currentTarget.previousElementSibling.style.visibility = 'hidden'; }, onError: e => { e.currentTarget.style.display = 'none'; } })),
              h('span', { className: 'token-swap-label' }, h('strong', null, ticker), h('span', { className: 'token-swap-name' }, coin.name.replace(/\s*\([^)]*\)\s*$/, '')), h('span', { className: 'token-swap-action' }, `Swap to ${ticker === 'USDT' ? 'BTC' : 'USDT'} →`)));
          })),

          !filtered.length && h('p', null, 'No supported tokens match your search.'),

          filtered.length > limit && h('button', { className: 'pair-more', onClick: () => setLimit(n => n + 60) }, 'More tokens')) :

        !pair ? h('div', { className: 'pair-message', role: 'alert' }, 'This token pair or network is not currently supported. ', h(Link, { to: '/swap' }, 'Choose another pair')) :

        h(React.Fragment, null,

          h('div', { className: 'pair-form' }, h(SwapForm, { key: location.pathname + location.search, initialFrom: currencyKey(pair.from), initialTo: currencyKey(pair.to), initialAmount: location.state?.amount || '', onPairChange: openPair })),

          h('section', { className: 'pair-details' },

            h('div', null, h('span', null, 'You send'), h('h2', null, pair.from.name), h('p', null, 'Network: ', pair.from.network)),

            h('div', null, h('span', null, 'You receive'), h('h2', null, pair.to.name), h('p', null, 'Network: ', pair.to.network))),

          h('section', { className: 'pair-guide' }, h('h2', null, `How to swap ${pair.from.ticker.toUpperCase()} to ${pair.to.ticker.toUpperCase()}`),

            h('p', null, copy.intro),

            h('ol', null, h('li', null, 'Enter the amount and confirm both networks.'), h('li', null, 'Compare live quotes and select a provider.'), h('li', null, 'Enter your recipient address and review the exchange before confirming.')),

            h('p', null, 'Quotes, limits and availability depend on the selected provider and networks. Use a recipient address that supports the receiving network.')),

          h('section', { className: 'pair-guide' }, h('h2', null, 'Networks for this swap'), h('p', null, copy.networkText),

            h('h3', null, `${pair.from.ticker.toUpperCase()} networks`), h('p', null, copy.fromNetworks),

            h('h3', null, `${pair.to.ticker.toUpperCase()} networks`), h('p', null, copy.toNetworks),

            pairIsAvailable(currencyKey(pair.to),currencyKey(pair.from)) && h(Link, { to: swapUrl(pair.to, pair.from) }, `Reverse pair: ${pair.to.ticker.toUpperCase()} to ${pair.from.ticker.toUpperCase()} →`)),

          h('section', { className: 'pair-guide pair-faq' }, h('h2', null, 'Frequently asked questions'),

            copy.faqs.map(([question, answer]) => h('details', { key: question }, h('summary', null, question), h('p', null, answer))))),

      h(CommonPairs));

  }

  return { Page, CommonPairs };

}

